import { animate, style, transition, trigger } from '@angular/animations';
import { AsyncPipe, NgClass, NgFor, NgIf } from '@angular/common';
import { AfterContentInit, ChangeDetectorRef, Component, ElementRef, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { IBreadcrumb } from '@shared/models/breadcrumb.interface';
import { AccessibleClickDirective } from '@shared/shared/directives/accessible-click.directive';
import { BaseComponent } from '@shared/shared/helpers/base-component';
import { BreadcrumbService } from '@shared/shared/services/breadcrumb-service';
import { SiteConfigService } from '@shared/shared/services/site-config.service';
import * as RouterStore from '@shared/store/router';
import { BreadcrumbObject } from '@shared/store/router/reducers/router-breadcrumbs.reducers';
import { transformStringToParamsObject } from '@shared/utils/filter-url-transformer-utils';
import { Observable, asyncScheduler, fromEvent, merge } from 'rxjs';
import { delay, map, share, startWith, throttleTime } from 'rxjs/operators';

@Component({
  selector: 'ten-breadcrumb',
  templateUrl: './breadcrumb.component.html',
  styleUrls: ['./breadcrumb.component.scss'],
  animations: [
    trigger('fadeInOut', [
      transition('void => *', [style({ opacity: 0 }), animate('250ms linear', style({ opacity: 1 }))]),
      transition('* => void', [style({ opacity: 1 }), animate('250ms linear', style({ opacity: 0 }))]),
    ]),
  ],
  standalone: true,
  imports: [NgIf, AsyncPipe, NgFor, NgClass, AccessibleClickDirective, TranslateModule],
})
export class BreadcrumbComponent extends BaseComponent implements AfterContentInit {
  public componentData: { layout: string };
  public breadCrumbs: Array<IBreadcrumb>;
  public breadCrumbs$: Observable<Array<IBreadcrumb>> = this.ngrxStore.select(RouterStore.getBreadcrumbList);
  public leftFadeVisible$: Observable<boolean>;
  public rightFadeVisible$: Observable<boolean>;

  @ViewChild('scrollContainer', { static: true }) public scrollContainer: ElementRef;

  constructor(
    private router: Router,
    private ngrxStore: Store<RouterStore.RouterState>,
    public breadcrumbService: BreadcrumbService,
    siteConf: SiteConfigService,
    public changeDetectorRef: ChangeDetectorRef
  ) {
    super(siteConf, 'breadcrumbs');
  }

  public ngAfterContentInit() {
    this.breadCrumbs$.subscribe((data) => {
      this.breadCrumbs = data;
      this.changeDetectorRef.detectChanges();
    });

    const scrollContainerEl: HTMLDivElement = this.scrollContainer.nativeElement;
    const change$ = merge(this.breadCrumbs$, fromEvent(window, 'resize'), fromEvent(scrollContainerEl, 'scroll')).pipe(
      startWith(''),
      delay(0),
      throttleTime(100, asyncScheduler, { trailing: true }),
      share()
    );

    this.leftFadeVisible$ = change$.pipe(map(() => scrollContainerEl.scrollLeft > 0));
    this.rightFadeVisible$ = change$.pipe(
      map(() => scrollContainerEl.scrollWidth > scrollContainerEl.offsetWidth + scrollContainerEl.scrollLeft)
    );
  }

  public navigateToUrl(link: BreadcrumbObject, isLast: boolean) {
    if (!isLast && !link.localSearchKey) {
      this.router.navigateByUrl(link.url);
    } else if (!isLast && link.localSearchKey) {
      this.navigateWithParams(link.url, link.localSearchKey);
    }
  }

  public navigateWithParams(url: string, storageKey: string) {
    const previousSearch = localStorage.getItem(storageKey);

    if (previousSearch?.startsWith('/')) {
      this.router.navigateByUrl(previousSearch);
      return;
    }

    if (previousSearch) {
      const newQueryParams: any = transformStringToParamsObject(previousSearch.slice(1));
      this.router.navigate([url], { queryParams: newQueryParams });
    } else {
      this.router.navigateByUrl(url);
    }
  }
}
